import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './ContractorDashboard.css';
import allSafeLogo from '../../../assets/cloudsafe-logo.png';
import { 
  FaClipboardList,
  FaFileAlt,
  FaTasks,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
  FaUserCircle,
  FaBell,
  FaSearch,
  FaUsers,
  FaBuilding,
  FaCertificate,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaChartLine,
  FaEye,
  FaDownload,
  FaPlay,
  FaCalendar,
  FaCamera,
  FaFileSignature,
  FaBars,
  FaTimes
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import {
  getContractorInspections,
  submitContractorInspectionReport,
  updateContractorInspectionStatus,
  uploadInspectionPhotos
} from '../../../services/contractorService';
import { getApiEndpoint } from '../../../config/api';

const ContractorInspections = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [inspections, setInspections] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filter, setFilter] = useState('all'); // all, scheduled, in_progress, completed, overdue
  const [sortBy, setSortBy] = useState('inspectionDate'); // inspectionDate, priority, status

  useEffect(() => {
    // Verify user is contractor
    const userRole = getUserRole();
    if (userRole !== 'contractor') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }

    fetchInspectionsData();
  }, [navigate]);

  const fetchInspectionsData = async () => {
    try {
      // Get user profile
      const profileResponse = await fetch(getApiEndpoint('/api/users/profile'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (profileResponse.ok) {
        const profileData = await profileResponse.json();
        setUserName(`${profileData.firstName} ${profileData.lastName}`);
      }

      const inspectionRows = await getContractorInspections();
      setInspections(inspectionRows.map((inspection) => ({
        id: inspection.id,
        title: inspection.title || inspection.name || `Inspection #${inspection.id}`,
        property: inspection.property_name || `Property #${inspection.property_id}`,
        propertyAddress: inspection.property_address || 'Address unavailable',
        client: inspection.client || 'Client',
        inspectionDate: inspection.scheduled_date || inspection.scheduledDate || new Date().toISOString().split('T')[0],
        inspectionTime: '09:00',
        priority: 'Medium',
        status: (inspection.status || 'scheduled').replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        type: inspection.type || 'Safety Inspection',
        estimatedDuration: 2,
        checklist: ['Site access', 'Condition check', 'Photo evidence', 'Notes'],
        description: inspection.description || inspection.notes || 'Inspection details not provided.',
        lastInspection: inspection.lastInspection || null,
        frequency: 'Scheduled',
        requirements: ['PPE', 'Camera'],
        completedItems: inspection.status === 'in_progress' ? 2 : 0,
        totalItems: 4,
        findings: inspection.findings,
        reportGenerated: inspection.status === 'completed'
      })));
      
    } catch (error) {
      console.error('Error fetching inspections data:', error);
      toast.error('Failed to load inspections data');
      setInspections([]);
    }
  };

  const loadSimulatedInspectionData = () => {
    setUserName('John Smith');
    
    // Simulated inspections data
    setInspections([
      {
        id: 1,
        title: 'Annual Roof Safety Inspection',
        property: 'ABC Corporate Tower',
        propertyAddress: '123 Business Blvd, Downtown',
        client: 'ABC Corporation',
        inspectionDate: '2024-04-15',
        inspectionTime: '09:00',
        priority: 'High',
        status: 'Scheduled',
        type: 'Roof Safety',
        estimatedDuration: 4,
        checklist: ['Anchor Points', 'Static Lines', 'Roof Condition', 'Safety Equipment'],
        description: 'Comprehensive annual roof safety inspection including all anchor points, static lines, and general roof condition assessment.',
        lastInspection: '2023-04-15',
        frequency: 'Annual',
        requirements: ['Safety Harness', 'Load Testing Equipment', 'Camera']
      },
      {
        id: 2,
        title: 'Fire Safety System Check',
        property: 'Riverside Shopping Center',
        propertyAddress: '456 River St, Westside',
        client: 'City Mall Management',
        inspectionDate: '2024-04-16',
        inspectionTime: '14:00',
        priority: 'Medium',
        status: 'In Progress',
        type: 'Fire Safety',
        estimatedDuration: 6,
        completedItems: 3,
        totalItems: 8,
        checklist: ['Fire Extinguishers', 'Smoke Detectors', 'Emergency Lighting', 'Fire Doors', 'Sprinkler System', 'Fire Alarms', 'Exit Signs', 'Assembly Points'],
        description: 'Quarterly fire safety system inspection covering all fire prevention and detection equipment.',
        lastInspection: '2024-01-16',
        frequency: 'Quarterly',
        requirements: ['Testing Equipment', 'Inspection Tags', 'Camera'],
        findings: 'Found 2 fire extinguishers requiring service. Smoke detectors on floor 2 need battery replacement.'
      },
      {
        id: 3,
        title: 'Electrical Safety Assessment',
        property: 'Medical Center',
        propertyAddress: '321 Health St, Central',
        client: 'HealthCare Group',
        inspectionDate: '2024-04-18',
        inspectionTime: '10:30',
        priority: 'High',
        status: 'Scheduled',
        type: 'Electrical Safety',
        estimatedDuration: 5,
        checklist: ['Circuit Breakers', 'RCD Testing', 'PAT Testing', 'Emergency Power', 'Grounding Systems'],
        description: 'Bi-annual electrical safety assessment including all critical systems and emergency backup power.',
        lastInspection: '2023-10-18',
        frequency: 'Bi-annual',
        requirements: ['Multimeter', 'RCD Tester', 'PAT Testing Kit', 'Safety Equipment']
      },
      {
        id: 4,
        title: 'Building Maintenance Inspection',
        property: 'Industrial Warehouse',
        propertyAddress: '789 Industrial Ave, East Side',
        client: 'Warehouse Corp',
        inspectionDate: '2024-04-12',
        inspectionTime: '08:00',
        priority: 'Medium',
        status: 'Completed',
        type: 'Building Maintenance',
        estimatedDuration: 3,
        completedDate: '2024-04-12',
        checklist: ['HVAC Systems', 'Plumbing', 'Structural Elements', 'Lighting', 'Access Systems'],
        description: 'Monthly building maintenance inspection covering all major building systems.',
        lastInspection: '2024-03-12',
        frequency: 'Monthly',
        requirements: ['Basic Tools', 'Flashlight', 'Camera'],
        findings: 'All systems functioning normally. Minor leak detected in basement plumbing - repair scheduled.',
        reportGenerated: true
      },
      {
        id: 5,
        title: 'Emergency Preparedness Audit',
        property: 'Downtown Office Complex',
        propertyAddress: '555 Main St, Downtown',
        client: 'Property Management Inc',
        inspectionDate: '2024-04-10',
        inspectionTime: '11:00',
        priority: 'Low',
        status: 'Overdue',
        type: 'Emergency Preparedness',
        estimatedDuration: 4,
        checklist: ['Emergency Plans', 'Evacuation Routes', 'Assembly Points', 'First Aid Kits', 'Defibrillators', 'Emergency Contacts'],
        description: 'Semi-annual emergency preparedness audit ensuring all emergency systems and procedures are current.',
        lastInspection: '2023-10-10',
        frequency: 'Semi-annual',
        requirements: ['Checklist', 'Camera', 'Testing Equipment']
      }
    ]);
  };

  const handleLogout = () => {
    clearToken();
    localStorage.removeItem('userRole');
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // Quick Action Handlers
  const handleScheduleInspection = () => {
    toast.info('Create new inspections from the Property Inspections module');
  };

  const handleUploadPhotos = () => {
    // Create a hidden file input to trigger photo upload
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        const inProgress = inspections.find((i) => i.status === 'In Progress') || inspections[0];
        if (!inProgress) {
          toast.warning('No inspections available for photo upload');
          return;
        }
        uploadInspectionPhotos(inProgress.id, files)
          .then(() => toast.success(`Uploaded ${files.length} photo(s)`))
          .catch((error) => toast.error(error.message));
      }
    };
    input.click();
  };

  const handleGenerateReport = () => {
    const completedInspections = inspections.filter(i => i.status === 'Completed');
    if (completedInspections.length === 0) {
      toast.warning('No completed inspections available for report generation');
    } else {
      toast.info('Generating inspection reports...');
      Promise.all(
        completedInspections.map((inspection) =>
          submitContractorInspectionReport(inspection.id, {
            findings: inspection.findings || 'Completed inspection.',
            recommendations: 'Review and approve.',
            status: 'completed'
          })
        )
      )
        .then(() => toast.success('Inspection reports generated successfully'))
        .catch((error) => toast.error(error.message));
    }
  };

  const handleStartInspection = () => {
    const scheduledInspections = inspections.filter(i => i.status === 'Scheduled');
    if (scheduledInspections.length === 0) {
      toast.warning('No scheduled inspections available to start');
    } else {
      toast.info('Starting next scheduled inspection...');
      handleStartInspectionById(scheduledInspections[0].id);
    }
  };

  const handleStartInspectionById = (inspectionId) => {
    updateContractorInspectionStatus(inspectionId, 'in_progress')
      .then(() => {
        setInspections(prev => prev.map(inspection => 
          inspection.id === inspectionId 
            ? { ...inspection, status: 'In Progress' }
            : inspection
        ));
        toast.success('Inspection started successfully');
      })
      .catch((error) => toast.error(error.message));
  };

  const handleCompleteInspection = (inspectionId) => {
    Promise.all([
      updateContractorInspectionStatus(inspectionId, 'completed'),
      submitContractorInspectionReport(inspectionId, {
        findings: 'Inspection completed.',
        recommendations: 'No additional actions required.',
        status: 'completed'
      })
    ])
      .then(() => {
        setInspections(prev => prev.map(inspection => 
          inspection.id === inspectionId 
            ? { 
                ...inspection, 
                status: 'Completed',
                completedDate: new Date().toISOString().split('T')[0],
                reportGenerated: true
              }
            : inspection
        ));
        toast.success('Inspection completed successfully');
      })
      .catch((error) => toast.error(error.message));
  };

  const getStatusIcon = (status) => {
    switch (status.toLowerCase()) {
      case 'completed': return <FaCheckCircle />;
      case 'in progress': return <FaClock />;
      case 'scheduled': return <FaCalendar />;
      case 'overdue': return <FaExclamationTriangle />;
      default: return <FaClipboardList />;
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    if (filter === 'all') return true;
    return inspection.status.toLowerCase().replace(' ', '_') === filter;
  });

  const sortedInspections = [...filteredInspections].sort((a, b) => {
    switch (sortBy) {
      case 'inspectionDate':
        return new Date(a.inspectionDate) - new Date(b.inspectionDate);
      case 'priority':
        const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  return (
    <div className="contractor-dashboard">
      <header className="contractor-header">
        <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
          {sidebarOpen ? <FaTimes /> : <FaBars />}
        </button>
        <img src={allSafeLogo} alt="CloudSafe" className="header-logo" />
        <div className="header-right">
          <nav className="nav-links">
            <Link to="/contractor/dashboard" className="nav-link">Dashboard</Link>
            <Link to="/contractor/tasks" className="nav-link">My Tasks</Link>
            <Link to="/contractor/inspections" className="nav-link active">Inspections</Link>
            <Link to="/contractor/reports" className="nav-link">Reports</Link>
          </nav>
          <div className="header-icons">
            <div className="icon"><FaSearch /></div>
            <div className="icon"><FaBell /></div>
            <div className="user-avatar-container" aria-haspopup="menu" aria-expanded={showUserMenu} {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="user-menu">
                  <Link to="/contractor/profile" onClick={() => setShowUserMenu(false)}>Profile</Link>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="contractor-layout">
        <div className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />
        <aside className={`contractor-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-section">
            <h3>Navigation</h3>
            <nav className="sidebar-nav">
              <Link to="/contractor/dashboard" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaChartLine /><span>Dashboard</span>
              </Link>
              <Link to="/contractor/tasks" className="nav-item" onClick={() => setSidebarOpen(false)}>
                <FaTasks /><span>My Tasks</span>
              </Link>
              <Link to="/contractor/inspections" className="nav-item active" onClick={() => setSidebarOpen(false)}>
                <FaClipboardList /><span>Inspections</span>
              </Link>
            </nav>
          </div>
          <div className="sidebar-section">
            <h3>Quick Actions</h3>
            <nav className="sidebar-nav">
              <button className="nav-item" onClick={() => { handleScheduleInspection(); setSidebarOpen(false); }}>
                <FaCalendar /><span>Schedule Inspection</span>
              </button>
              <button className="nav-item" onClick={() => { handleUploadPhotos(); setSidebarOpen(false); }}>
                <FaCamera /><span>Upload Photos</span>
              </button>
              <button className="nav-item" onClick={() => { handleGenerateReport(); setSidebarOpen(false); }}>
                <FaFileSignature /><span>Generate Report</span>
              </button>
            </nav>
          </div>
        </aside>

        <main className="contractor-content">
          <div className="con-page-header">
            <div>
              <h1>Inspections</h1>
              <p className="con-page-subtitle">Manage scheduled inspections and safety assessments</p>
            </div>
            <div className="header-actions">
              <button className="btn-secondary" onClick={handleScheduleInspection}><FaCalendar /> Schedule New</button>
              <button className="btn-primary" onClick={handleStartInspection}><FaClipboardList /> Start Inspection</button>
            </div>
          </div>

          <div className="stats-grid">
            <div className="stat-card pending">
              <div className="stat-icon"><FaCalendar /></div>
              <div className="stat-content">
                <h3>Scheduled</h3>
                <p className="stat-number">{inspections.filter(i => i.status === 'Scheduled').length}</p>
              </div>
            </div>
            <div className="stat-card completed">
              <div className="stat-icon"><FaClock /></div>
              <div className="stat-content">
                <h3>In Progress</h3>
                <p className="stat-number">{inspections.filter(i => i.status === 'In Progress').length}</p>
              </div>
            </div>
            <div className="stat-card clients">
              <div className="stat-icon"><FaCheckCircle /></div>
              <div className="stat-content">
                <h3>Completed</h3>
                <p className="stat-number">{inspections.filter(i => i.status === 'Completed').length}</p>
              </div>
            </div>
            <div className="stat-card overdue">
              <div className="stat-icon"><FaExclamationTriangle /></div>
              <div className="stat-content">
                <h3>Overdue</h3>
                <p className="stat-number">{inspections.filter(i => i.status === 'Overdue').length}</p>
              </div>
            </div>
          </div>

          <div className="task-controls">
            <div className="filter-controls">
              <label>Status:</label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                <option value="all">All Inspections</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div className="sort-controls">
              <label>Sort:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="inspectionDate">Inspection Date</option>
                <option value="priority">Priority</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>

          <div className="tasks-list">
            {sortedInspections.map(inspection => (
              <div key={inspection.id} className="task-card">
                <div className="task-header">
                  <div className="task-info">
                    <h3>{inspection.title}</h3>
                    <div className="task-meta">
                      <span className="property-name"><FaBuilding /> {inspection.property}</span>
                      <span className="client-name"><FaUsers /> {inspection.client}</span>
                    </div>
                  </div>
                  <div className="task-status-info">
                    <span className={`priority ${inspection.priority.toLowerCase()}`}>{inspection.priority}</span>
                    <span className={`status ${inspection.status.toLowerCase().replace(' ', '-')}`}>
                      {getStatusIcon(inspection.status)} {inspection.status}
                    </span>
                  </div>
                </div>

                <div className="task-description"><p>{inspection.description}</p></div>

                <div className="task-details">
                  <div className="detail-grid">
                    <div className="detail-item"><FaMapMarkerAlt className="icon" /><span>{inspection.propertyAddress}</span></div>
                    <div className="detail-item"><FaCalendarAlt className="icon" /><span>{inspection.inspectionDate} at {inspection.inspectionTime}</span></div>
                    <div className="detail-item"><FaClock className="icon" /><span>Duration: {inspection.estimatedDuration}h</span></div>
                    <div className="detail-item"><FaCertificate className="icon" /><span>{inspection.type} &middot; {inspection.frequency}</span></div>
                  </div>
                </div>

                <div className="inspection-checklist">
                  <h4>Inspection Items:</h4>
                  <div className="checklist-items">
                    {inspection.checklist.slice(0, 4).map((item, index) => (
                      <span key={index} className="checklist-item">
                        {inspection.status === 'In Progress' && inspection.completedItems > index
                          ? <FaCheckCircle style={{ color: 'var(--cs-success)' }} />
                          : <FaClock style={{ color: 'var(--cs-gray-400)' }} />}
                        {item}
                      </span>
                    ))}
                    {inspection.checklist.length > 4 && (
                      <span className="checklist-item">+{inspection.checklist.length - 4} more</span>
                    )}
                  </div>
                </div>

                {inspection.status === 'In Progress' && (
                  <div className="task-progress">
                    <div className="progress-info">
                      <span>Progress: {inspection.completedItems || 0}/{inspection.totalItems || inspection.checklist.length} items</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${((inspection.completedItems || 0) / (inspection.totalItems || inspection.checklist.length)) * 100}%` }} />
                    </div>
                  </div>
                )}

                {inspection.findings && (
                  <div className="inspection-findings">
                    <h4>Findings:</h4>
                    <p>{inspection.findings}</p>
                  </div>
                )}

                <div className="task-actions">
                  {inspection.status === 'Scheduled' && (
                    <button className="action-btn start" onClick={() => handleStartInspectionById(inspection.id)}><FaPlay /> Start</button>
                  )}
                  {inspection.status === 'In Progress' && (
                    <>
                      <button className="action-btn"><FaCamera /> Photos</button>
                      <button className="action-btn complete" onClick={() => handleCompleteInspection(inspection.id)}><FaCheckCircle /> Complete</button>
                    </>
                  )}
                  {inspection.status === 'Completed' && inspection.reportGenerated && (
                    <button className="action-btn"><FaDownload /> Report</button>
                  )}
                  <button className="action-btn"><FaEye /> Details</button>
                  <button className="action-btn"><FaMapMarkerAlt /> Directions</button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ContractorInspections; 